import { useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  CurrencyIcon,
  CurrencyIconPair,
  CurrencyIconSlot,
} from "../../../Components/Atoms/CurrencyIcon";
import {
  GRID_HISTORY_COLS_4,
  GRID_HISTORY_HEADER_VALUE_CLASS,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryRow,
  GridHistoryStatusActionsCell,
} from "../../../Components/Molecules/GridHistory";
import { StablesHistoryListSkeleton } from "../../../Components/Molecules/DashboardSkeleton";
import { useStablesHistory } from "../../../Hooks/useStablesHistory";
import { useStablesHistoryFilterOptions } from "../../../Hooks/useStablesHistoryFilterOptions";
import { StablesHistoryDetailModal } from "./StablesHistoryDetailModal";
import { StablesHistoryFilterBar } from "./StablesHistoryFilterBar";
import { StablesHistoryTypeTabs } from "./StablesHistoryTypeTabs";
import { formatQuotePrice } from "./stablesQuoteDisplay";
import {
  getGeneralHistoryTabs,
  getHistoryTabs,
  formatBrl,
  formatPurchaseHistoryTitle,
  formatSaleHistoryTitle,
  formatStable,
  formatSwapHistoryTitle,
  formatWithdrawalDestination,
  formatWithdrawalHistoryAmount,
  formatWithdrawalHistoryFeeDetail,
  formatWithdrawalHistoryTitle,
} from "./constants";
import { paginateList } from "../Extract/extractFilters";
import {
  EMPTY_STABLES_HISTORY_FILTERS,
  applyStablesClientFilters,
} from "./stablesHistoryFilters";
import { StablesHistoryPagination } from "./components/StablesHistoryPagination";
import {
  StablesGeneralHistoryGrid,
  StablesHistoryHeaderRow,
  StablesHistoryStatusBadge,
} from "./components/StablesGeneralHistoryGrid";

const DEFAULT_ROWS_PER_PAGE = 10;
const STABLES_CLIENT_SEARCH_PAGE_SIZE = 5000;

const StatusActionsCell = ({ onShowDetail, item, status }) => (
  <GridHistoryStatusActionsCell
    statusLabel={intl.get("grid.status")}
    statusBadge={<StablesHistoryStatusBadge status={status} />}
    onShowDetail={onShowDetail}
    item={item}
  />
);

const EmptyState = ({ message }) => (
  <div className="app-empty-state m-4">{message}</div>
);

const PurchasesHistory = ({ items, onShowDetail }) =>
  items.map((item) => (
    <GridHistoryRow key={item.id} colSpan={4}>
      <GridHistoryCell label={intl.get("grid.transaction")}>
        <div className="flex min-w-0 items-center gap-3">
          <CurrencyIconSlot>
            <CurrencyIcon asset={item.asset} />
          </CurrencyIconSlot>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {formatPurchaseHistoryTitle(item)}
          </p>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.date")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{item.date}</p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.value")} align="right">
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {formatBrl(item.amountBrl)}
        </p>
        {Number(item.rate) > 0 ? (
          <p className="text-[11px] text-gray-400">
            {intl.get("stables.quoteRate", { rate: formatQuotePrice(item.rate) })}
          </p>
        ) : null}
      </GridHistoryCell>
      <StatusActionsCell onShowDetail={onShowDetail} item={item} status={item.status} />
    </GridHistoryRow>
  ));

const SalesHistory = ({ items, onShowDetail }) =>
  items.map((item) => (
    <GridHistoryRow key={item.id} colSpan={4}>
      <GridHistoryCell label={intl.get("grid.transaction")}>
        <div className="flex min-w-0 items-center gap-3">
          <CurrencyIconSlot>
            <CurrencyIcon asset={item.from} />
          </CurrencyIconSlot>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {formatSaleHistoryTitle(item)}
          </p>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.date")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{item.date}</p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.value")} align="right">
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {formatBrl(item.amountTo)}
        </p>
        {Number(item.rate) > 0 ? (
          <p className="text-[11px] text-gray-400">
            {intl.get("stables.quoteRate", { rate: formatQuotePrice(item.rate) })}
          </p>
        ) : null}
      </GridHistoryCell>
      <StatusActionsCell onShowDetail={onShowDetail} item={item} status={item.status} />
    </GridHistoryRow>
  ));

const SwapsHistory = ({ items, onShowDetail }) =>
  items.map((item) => (
    <GridHistoryRow key={item.id} colSpan={4}>
      <GridHistoryCell label={intl.get("grid.transaction")}>
        <div className="flex min-w-0 items-center gap-3">
          <CurrencyIconSlot>
            <CurrencyIconPair from={item.from} to={item.to} />
          </CurrencyIconSlot>
          <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
            {formatSwapHistoryTitle(item)}
          </p>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.date")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{item.date}</p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.value")} align="right">
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {formatStable(item.amountTo, item.to)}
        </p>
        <p className="text-[11px] text-gray-400">
          {intl.get("stables.sentAmount", { amount: formatStable(item.amountFrom, item.from) })}
        </p>
      </GridHistoryCell>
      <StatusActionsCell onShowDetail={onShowDetail} item={item} status={item.status} />
    </GridHistoryRow>
  ));

const WithdrawalsHistory = ({ items, onShowDetail }) =>
  items.map((item) => (
    <GridHistoryRow key={item.id} colSpan={4}>
      <GridHistoryCell label={intl.get("grid.transaction")}>
        <div className="flex min-w-0 items-center gap-3">
          <CurrencyIconSlot>
            <CurrencyIcon asset={item.asset} />
          </CurrencyIconSlot>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
              {formatWithdrawalHistoryTitle(item)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{formatWithdrawalDestination(item.destination)}</p>
          </div>
        </div>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.date")}>
        <p className="text-xs text-gray-500 dark:text-gray-400">{item.date}</p>
      </GridHistoryCell>
      <GridHistoryCell label={intl.get("grid.value")} align="right">
        <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {formatWithdrawalHistoryAmount(item)}
        </p>
        <p className="text-[11px] text-gray-400">{formatWithdrawalHistoryFeeDetail(item)}</p>
      </GridHistoryCell>
      <StatusActionsCell onShowDetail={onShowDetail} item={item} status={item.status} />
    </GridHistoryRow>
  ));

const HISTORY_CONTENT = {
  purchases: PurchasesHistory,
  sales: SalesHistory,
  swaps: SwapsHistory,
  withdrawals: WithdrawalsHistory,
};

export const StablesHistoryPanel = ({
  historyType,
  title,
  refreshToken = 0,
  historyTab,
  useHistoryRoutes = false,
}) => {
  const isGeneral = historyType === "general";
  const historyTabs = isGeneral ? getGeneralHistoryTabs() : getHistoryTabs();
  const resolvedInitialTab = historyTab ?? (isGeneral ? "all" : historyType || "purchases");
  const [activeTab, setActiveTab] = useState(resolvedInitialTab);
  const [selectedItem, setSelectedItem] = useState(null);
  const [filters, setFilters] = useState(EMPTY_STABLES_HISTORY_FILTERS);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  useEffect(() => {
    if (historyTab != null) {
      setActiveTab(historyTab);
      setPage(1);
    }
  }, [historyTab]);

  const resolvedApiType = isGeneral
    ? (activeTab === "all" ? "all" : activeTab)
    : (historyType || activeTab);

  const showTabs = isGeneral || !historyType;
  const hasClientSearch = Boolean(filters.searchQuery?.trim());

  const {
    items: apiItems,
    total: apiTotal,
    totalPages: apiTotalPages,
    loading,
    error,
    reload,
  } = useStablesHistory({
    type: resolvedApiType,
    page: hasClientSearch ? 1 : page,
    pageSize: hasClientSearch ? STABLES_CLIENT_SEARCH_PAGE_SIZE : rowsPerPage,
    filters,
  });

  const { tabCounts } = useStablesHistoryFilterOptions({
    enabled: showTabs,
    refreshToken,
  });

  useEffect(() => {
    if (refreshToken > 0) {
      reload();
    }
  }, [refreshToken, reload]);

  const searchFilteredItems = useMemo(
    () => applyStablesClientFilters(apiItems, filters),
    [apiItems, filters],
  );

  const totalItems = hasClientSearch ? searchFilteredItems.length : apiTotal;
  const totalPages = hasClientSearch
    ? Math.max(1, Math.ceil(searchFilteredItems.length / rowsPerPage))
    : apiTotalPages;

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const displayItems = useMemo(() => {
    if (!hasClientSearch) return searchFilteredItems;
    return paginateList(searchFilteredItems, page, rowsPerPage);
  }, [hasClientSearch, searchFilteredItems, page, rowsPerPage]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
  };

  const handleTabChange = (nextTab) => {
    setActiveTab(nextTab);
    setPage(1);
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const resolvedType = isGeneral ? null : (historyType || activeTab);
  const Content = isGeneral ? null : HISTORY_CONTENT[resolvedType];

  const renderHistoryRows = () => {
    if (isGeneral) {
      return (
        <StablesGeneralHistoryGrid
          items={displayItems}
          onShowDetail={setSelectedItem}
        />
      );
    }
    return (
      <GridHistoryContainer gridCols={GRID_HISTORY_COLS_4}>
        <StablesHistoryHeaderRow />
        <Content items={displayItems} onShowDetail={setSelectedItem} />
      </GridHistoryContainer>
    );
  };

  return (
    <section>
      <h2 className="mb-3 text-sm font-bold text-brand-dark dark:text-gray-100">{title}</h2>
      <div className="mb-4 space-y-3">
        {showTabs && (
          <StablesHistoryTypeTabs
            tabs={historyTabs}
            counts={tabCounts}
            activeTab={activeTab}
            onChange={handleTabChange}
            useHistoryRoutes={useHistoryRoutes}
          />
        )}
        <StablesHistoryFilterBar
          filters={filters}
          onFiltersChange={handleFiltersChange}
          filteredItems={searchFilteredItems}
          historyType={resolvedApiType}
        />
      </div>
      <div className="app-card overflow-hidden">
        {loading ? (
          <StablesHistoryListSkeleton rows={rowsPerPage} />
        ) : error && displayItems.length === 0 ? (
          <EmptyState message={intl.get("stables.historyLoadError") || "Não foi possível carregar o histórico."} />
        ) : totalItems === 0 ? (
          <EmptyState message={intl.get("stables.noOperationsFiltered")} />
        ) : displayItems.length === 0 ? (
          <EmptyState message={intl.get("stables.noOperationsOnPage")} />
        ) : (
          renderHistoryRows()
        )}

        <div className="mt-4">
          <StablesHistoryPagination
            total={totalItems}
            page={page}
            rowsPerPage={rowsPerPage}
            currentPageCount={displayItems.length}
            totalPages={totalPages}
            onPageChange={setPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            itemLabel={intl.get("stables.operationsItemLabel")}
          />
        </div>
      </div>

      <StablesHistoryDetailModal
        open={Boolean(selectedItem)}
        onClose={() => setSelectedItem(null)}
        item={selectedItem}
      />
    </section>
  );
};
